/**
 * Legal Re-Acceptance Modal
 * Shown when a user needs to re-accept updated Terms of Service or Privacy Policy
 */

import { useAuth } from '@contexts/AuthContext';
import { Check, ChevronRight, FileText, ShieldCheck } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { LegalModal } from './LegalModal';

type LegalDocType = 'terms' | 'privacy';

export function LegalReAcceptanceModal(): ReactElement | null {
  const { user, acceptLegalDocuments, logout } = useAuth();
  const [showLegalDoc, setShowLegalDoc] = useState<LegalDocType | null>(null);
  const [viewedDocs, setViewedDocs] = useState({
    terms: false,
    privacy: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show if user needs to re-accept legal documents
  if (!user?.needsLegalReAcceptance) {
    return null;
  }

  const canAccept = viewedDocs.terms && viewedDocs.privacy;

  const openDoc = (type: LegalDocType): void => {
    setShowLegalDoc(type);
  };

  const closeDoc = (): void => {
    if (showLegalDoc) {
      setViewedDocs((prev) => ({ ...prev, [showLegalDoc]: true }));
    }
    setShowLegalDoc(null);
  };

  const handleAccept = async (): Promise<void> => {
    if (!canAccept) return;

    setLoading(true);
    setError(null);

    try {
      await acceptLegalDocuments();
    } catch (err) {
      setError((err as Error).message || 'Failed to accept. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = (): void => {
    // If they decline, log them out
    logout();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop - not clickable to dismiss */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative bg-theme-surface rounded-lg w-full max-w-lg border border-theme-border shadow-xl">
          {/* Header */}
          <div className="px-6 py-4 border-b border-theme-border">
            <h2 className="text-xl font-bold text-theme-text">Updated Terms</h2>
            <p className="text-sm text-theme-text-secondary mt-1">
              We've updated our Terms of Service and Privacy Policy. Please review and accept to
              continue.
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-theme-danger/50 border border-theme-danger rounded text-theme-text-inverse text-sm">
                {error}
              </div>
            )}

            {/* Document links */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => openDoc('terms')}
                className={`w-full flex items-center gap-3 p-3 rounded border transition ${
                  viewedDocs.terms
                    ? 'bg-theme-success/20 border-theme-success text-theme-success'
                    : 'bg-theme-surface-hover border-theme-border text-theme-text hover:bg-theme-surface-active'
                }`}
              >
                {viewedDocs.terms ? (
                  <Check className="w-5 h-5 shrink-0" />
                ) : (
                  <FileText className="w-5 h-5 shrink-0" />
                )}
                <span className="flex-1 text-left font-medium">
                  {viewedDocs.terms ? 'Terms of Service (reviewed)' : 'Review Terms of Service'}
                </span>
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => openDoc('privacy')}
                className={`w-full flex items-center gap-3 p-3 rounded border transition ${
                  viewedDocs.privacy
                    ? 'bg-theme-success/20 border-theme-success text-theme-success'
                    : 'bg-theme-surface-hover border-theme-border text-theme-text hover:bg-theme-surface-active'
                }`}
              >
                {viewedDocs.privacy ? (
                  <Check className="w-5 h-5 shrink-0" />
                ) : (
                  <ShieldCheck className="w-5 h-5 shrink-0" />
                )}
                <span className="flex-1 text-left font-medium">
                  {viewedDocs.privacy ? 'Privacy Policy (reviewed)' : 'Review Privacy Policy'}
                </span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {!canAccept && (
              <p className="text-xs text-theme-text-tertiary text-center">
                Please review both documents before accepting
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-theme-border flex gap-3">
            <button
              type="button"
              onClick={handleDecline}
              className="flex-1 py-2 text-theme-text-secondary hover:text-theme-text font-medium rounded transition"
            >
              Decline & Log Out
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!canAccept || loading}
              className="flex-1 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface-hover disabled:text-theme-text-tertiary text-theme-on-primary-fill font-medium rounded transition"
            >
              {loading ? 'Accepting...' : 'I Accept'}
            </button>
          </div>
        </div>
      </div>

      {/* Nested Legal Document Modal */}
      {showLegalDoc && <LegalModal type={showLegalDoc} onClose={closeDoc} />}
    </>
  );
}
